/**
 * component-filter-grid.js
 *
 * Circular "coin" filter buttons rendered above the collection product grid.
 * Clicking a coin applies/removes its filter query via AJAX (no full page reload)
 * by reusing the collection facets AJAX pipeline from component-facets.js.
 */
(function () {
  'use strict';

  if (typeof window.Woolman == 'undefined') {
    window.Woolman = {};
  }

  const FILTER_PREFIX = 'filter.';
  const ACTIVE_CLASS = 'filter-grid-item--active';

  const AJAX_SELECTORS = [
    '#product-grid-ajax',
    '#products-count-ajax',
    '#current-facets-ajax',
    '#pagination-ajax',
  ];

  /**
   * Read current URL params into Shopify.queryParams (Taiga convention:
   * multi-value filters are comma-joined strings).
   */
  const prepareQueryParams = () => {
    Shopify.queryParams = {};
    Woolman.Utils.prepareQueryParams();
    return Shopify.queryParams;
  };

  /**
   * Parse a "k=v&k2=v2" filter query string into Shopify.queryParams entries.
   */
  const parseFilterQuery = (filterQuery) => {
    const entries = {};
    new URLSearchParams(filterQuery).forEach((value, key) => {
      if (entries[key]) {
        entries[key].push(value);
      } else {
        entries[key] = [value];
      }
    });
    Object.keys(entries).forEach((key) => {
      entries[key] = entries[key].join(',');
    });
    return entries;
  };

  /**
   * Remove all filter.* params from Shopify.queryParams.
   */
  const clearFilterParams = () => {
    Object.keys(Shopify.queryParams).forEach((key) => {
      if (key.startsWith(FILTER_PREFIX)) {
        delete Shopify.queryParams[key];
      }
    });
  };

  /**
   * Uncheck: removes this item's filters from current params, keeps others.
   */
  const buildUncheckParams = (filterQuery) => {
    const itemFilters = parseFilterQuery(filterQuery);
    Object.keys(itemFilters).forEach((key) => {
      const removeValues = itemFilters[key].split(',');
      const current = Shopify.queryParams[key];
      if (!current) return;
      const kept = current
        .split(',')
        .filter((value) => !removeValues.includes(value));
      if (kept.length > 0) {
        Shopify.queryParams[key] = kept.join(',');
      } else {
        delete Shopify.queryParams[key];
      }
    });
  };

  /**
   * Check: clears all other filter.* params, applies this item's filters.
   */
  const buildCheckParams = (filterQuery) => {
    clearFilterParams();
    Object.assign(Shopify.queryParams, parseFilterQuery(filterQuery));
  };

  /**
   * Is this item's filter query fully represented in current params?
   */
  const isItemActive = (filterQuery, queryParams) => {
    const itemFilters = parseFilterQuery(filterQuery);
    const filterKeys = Object.keys(queryParams).filter((key) =>
      key.startsWith(FILTER_PREFIX)
    );
    const itemValues = Object.values(itemFilters).join(',').split(',');

    const matches = Object.keys(itemFilters).every((key) => {
      const current = queryParams[key];
      if (!current) return false;
      const currentValues = current.split(',');
      return itemFilters[key].split(',').every((value) =>
        currentValues.includes(value)
      );
    });

    const totalActiveCount = filterKeys.reduce((count, key) => {
      return count + queryParams[key].split(',').length;
    }, 0);

    return matches && totalActiveCount === itemValues.length;
  };

  /**
   * Recompute active states for all coin items based on current params.
   */
  const updateActiveStates = () => {
    const queryParams = Shopify.queryParams || prepareQueryParams();
    document.querySelectorAll('.filter-grid-item').forEach((item) => {
      const filterQuery = item.dataset.filterQuery;
      if (!filterQuery) return;
      const isActive = isItemActive(filterQuery, queryParams);
      item.classList.toggle(ACTIVE_CLASS, isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  /**
   * Sync every facets-form checkbox with the current query params so the
   * drawer/sidebar checkboxes reflect coin selections.
   */
  const syncCheckboxStates = () => {
    document
      .querySelectorAll('facets-form input[type="checkbox"]')
      .forEach((checkbox) => {
        const paramName = checkbox.getAttribute('name');
        if (!paramName || !paramName.includes(FILTER_PREFIX)) return;
        const paramValues = Shopify.queryParams[paramName]
          ? Shopify.queryParams[paramName].split(',')
          : [];
        checkbox.checked = paramValues.includes(checkbox.value);
      });
  };

  /**
   * Reset price range sliders when coin selection removes active price filters,
   * otherwise stale slider UI state would re-add them on next drawer Apply.
   */
  const resetPriceSliders = () => {
    const hasPriceParams =
      Shopify.queryParams['filter.v.price.gte'] ||
      Shopify.queryParams['filter.v.price.lte'];

    if (!hasPriceParams) {
      if (typeof PriceRangeSlider !== 'undefined' && PriceRangeSlider.resetAll) {
        PriceRangeSlider.resetAll();
      }
      document.querySelectorAll('price-range-slider').forEach((slider) => {
        slider.removeAttribute('data-updated');
      });
    }
  };

  const startLoading = () => {
    if (Woolman.Collection) {
      Woolman.Collection.classList.add('processing');
    }
  };

  const onClick = (event) => {
    const el = event.currentTarget;

    const filterQuery = el.dataset.filterQuery;
    if (!filterQuery) return;

    // Fallback: no facets AJAX pipeline available (filtering disabled).
    // Navigate while preserving existing query params (sort, page, etc.)
    if (!Woolman.Facets || typeof Woolman.Facets.getCollectionAJAX !== 'function') {
      event.preventDefault();
      prepareQueryParams();

      if (el.classList.contains(ACTIVE_CLASS)) {
        buildUncheckParams(filterQuery);
      } else {
        buildCheckParams(filterQuery);
      }

      const params = new URLSearchParams(
        Object.entries(Shopify.queryParams)
      ).toString().replaceAll('%2C', ',');
      window.location.href = `${el.dataset.collectionUrl}${params ? '?' + params : ''}`;
      return;
    }

    event.preventDefault();

    prepareQueryParams();

    if (el.classList.contains(ACTIVE_CLASS)) {
      buildUncheckParams(filterQuery);
    } else {
      buildCheckParams(filterQuery);
    }
    resetPriceSliders();

    startLoading();
    Woolman.Facets.getCollectionAJAX(AJAX_SELECTORS).finally(() => {
      updateActiveStates();
      syncCheckboxStates();
      if (Woolman.Collection) {
        Woolman.Collection.classList.remove('processing');
      }
    });
  };

  const bindListeners = () => {
    document.querySelectorAll('.filter-grid-item').forEach((item) => {
      item.addEventListener('click', onClick);
    });
  };

  document.addEventListener('collection:updated', () => {
    updateActiveStates();
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateActiveStates();
    bindListeners();
  });

  // Rebind after any AJAX re-render of the page content and refresh states
  document.addEventListener('shopify:section:load', () => {
    bindListeners();
    updateActiveStates();
  });
})();
